import { useCallback, useEffect, useRef } from 'react';
import useUpdateSubscriptionMutation from '@/api/mutations/useUpdateSubscriptionMutation';
import useGetBillingInfoQuery from '@/api/queries/useGetBillingInfoQuery';
import useListPaymentMethodsQuery from '@/api/queries/useListPaymentMethodsQuery';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { isBillingAddressComplete } from '@/models/Billing';
import {
  BillingDetailsEditPath,
  BillingHomePath,
  BillingUpgradePlanPaymentSetupPath,
} from '@/modules/billing/paths';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import { ArrowUpCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const UpgradePlan = () => {
  const navigate = useNavigate();
  const autoUpgradeStarted = useRef(false);
  const autoRedirectStarted = useRef(false);
  const updateSubscription = useUpdateSubscriptionMutation();
  const {
    billingInfo,
    isLoading: isLoadingBillingInfo,
    error: billingInfoError,
  } = useGetBillingInfoQuery();
  const {
    paymentMethods,
    isLoading: isLoadingPaymentMethods,
    error: paymentMethodsError,
  } = useListPaymentMethodsQuery();

  const hasDefaultPaymentMethod = Boolean(
    paymentMethods?.items?.some((method) => method.card?.isDefault && method.card?.isActive),
  );
  const hasCompleteBillingAddress = isBillingAddressComplete(billingInfo?.billingDetails?.address);

  const upgradeToPaidPlan = useCallback(async () => {
    await updateSubscription.mutateAsync({
      plan: 'paid',
      billingCycle: 'monthly',
    });

    toast.custom((t) => (
      <CustomToast id={t.id} message={'Plan upgraded successfully!'} visible={t.visible} />
    ));

    navigate(BillingHomePath, {
      state: { upgraded: true },
    });
  }, [navigate, updateSubscription]);

  useEffect(() => {
    if (
      isLoadingBillingInfo ||
      !hasCompleteBillingAddress ||
      isLoadingPaymentMethods ||
      !hasDefaultPaymentMethod ||
      autoUpgradeStarted.current
    ) {
      return;
    }

    autoUpgradeStarted.current = true;
    void upgradeToPaidPlan().catch((upgradeError) => {
      console.error('Error upgrading plan:', upgradeError);
    });
  }, [
    hasCompleteBillingAddress,
    hasDefaultPaymentMethod,
    isLoadingBillingInfo,
    isLoadingPaymentMethods,
    upgradeToPaidPlan,
  ]);

  useEffect(() => {
    if (isLoadingBillingInfo || billingInfoError || hasCompleteBillingAddress) {
      return;
    }

    navigate(BillingDetailsEditPath, { replace: true });
  }, [billingInfoError, hasCompleteBillingAddress, isLoadingBillingInfo, navigate]);

  useEffect(() => {
    if (
      isLoadingPaymentMethods ||
      isLoadingBillingInfo ||
      !hasCompleteBillingAddress ||
      hasDefaultPaymentMethod ||
      paymentMethodsError ||
      autoRedirectStarted.current
    ) {
      return;
    }

    autoRedirectStarted.current = true;
    navigate(BillingUpgradePlanPaymentSetupPath, { replace: true });
  }, [
    hasCompleteBillingAddress,
    hasDefaultPaymentMethod,
    isLoadingBillingInfo,
    isLoadingPaymentMethods,
    navigate,
    paymentMethodsError,
  ]);

  if (
    isLoadingBillingInfo ||
    (!billingInfoError && !hasCompleteBillingAddress) ||
    isLoadingPaymentMethods ||
    (hasDefaultPaymentMethod && updateSubscription.isPending)
  ) {
    return (
      <>
        <SettingsHeader title="Upgrade Plan" icon={<ArrowUpCircleIcon className="w-6 h-6" />} />
        <div className="flex justify-center py-20">
          <SpinnerIcon />
        </div>
      </>
    );
  }

  if (billingInfoError || paymentMethodsError || updateSubscription.error) {
    return (
      <>
        <SettingsHeader title="Upgrade Plan" icon={<ArrowUpCircleIcon className="w-6 h-6" />} />
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          Unable to upgrade your plan. Please try again.
        </div>
      </>
    );
  }

  if (hasDefaultPaymentMethod) {
    return (
      <>
        <SettingsHeader title="Upgrade Plan" icon={<ArrowUpCircleIcon className="w-6 h-6" />} />
        <div className="flex justify-center py-20">
          <SpinnerIcon />
        </div>
      </>
    );
  }

  return (
    <>
      <SettingsHeader title="Upgrade Plan" icon={<ArrowUpCircleIcon className="w-6 h-6" />} />
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    </>
  );
};

export default UpgradePlan;
